import { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef, ReactNode } from 'react';
import { tenantApi } from '@/modules/tenant/services/tenantApi';
import { useTenantAuth } from '@/modules/tenant/hooks/useTenantAuth';
import { readBootstrap } from '@/lib/readBootstrap';

interface ModuleContextType {
  modules: string[];
  hasModule: (moduleName: string) => boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

interface ModuleProviderProps {
  children: ReactNode;
}

const MODULES_CACHE_KEY = 'tenant-modules';

export function ModuleProvider({ children }: ModuleProviderProps) {
  // Prefer the server-injected island (W4 D3) over the localStorage cache so
  // the first render gates UI on the authoritative module list with no flash;
  // the /auth/modules fetch below still revalidates.
  const getInitialModules = (): string[] => {
    const islandModules = readBootstrap()?.modules;
    if (islandModules && islandModules.length) return islandModules;
    try {
      const cached = localStorage.getItem(MODULES_CACHE_KEY);
      return cached ? JSON.parse(cached) : ['core'];
    } catch {
      return ['core'];
    }
  };

  const [modules, setModules] = useState<string[]>(getInitialModules());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useTenantAuth();
  const isFetching = useRef(false);
  const retryCount = useRef(0);

  // Stable identity: only touches state setters and refs, all of which React
  // guarantees are stable — so an empty dep list is correct and lets the effect
  // below fire on auth changes alone. The recursive retry call resolves to this
  // same memoized reference.
  const fetchModules = useCallback(async () => {
    if (isFetching.current) return;

    try {
      isFetching.current = true;
      setIsLoading(true);
      setError(null);

      const data = await tenantApi.getOne<{ modules: string[] }>('/auth/modules');
      const freshModules = data.modules || ['core'];

      setModules(freshModules);
      localStorage.setItem(MODULES_CACHE_KEY, JSON.stringify(freshModules));
      retryCount.current = 0;
    } catch (err) {
      console.error('Failed to fetch modules:', err);
      setError(err as Error);
      // Retry up to 2 times with delay (session may not be ready yet)
      if (retryCount.current < 2) {
        retryCount.current += 1;
        setTimeout(() => {
          isFetching.current = false;
          fetchModules();
        }, 1000);
        return;
      }
      retryCount.current = 0;
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Always fetch fresh modules when auth state changes to authenticated
      fetchModules();
    } else {
      localStorage.removeItem(MODULES_CACHE_KEY);
      setModules(['core']);
      setIsLoading(false);
      retryCount.current = 0;
    }
  }, [isAuthenticated, fetchModules]);

  const hasModule = useCallback(
    (moduleName: string): boolean => modules.includes(moduleName),
    [modules],
  );

  const value: ModuleContextType = useMemo(
    () => ({ modules, hasModule, isLoading, error, refetch: fetchModules }),
    [modules, hasModule, isLoading, error, fetchModules],
  );

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
}

export function useModules() {
  const context = useContext(ModuleContext);
  
  if (context === undefined) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  
  return context;
}
